import React, { useMemo } from 'react';
import { Select, SelectItem, SelectSection, Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useCategories, Category } from '../../../hooks/queries/useCategories';

export type { Category };

interface CategorySelectorProps {
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showAllOption?: boolean;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
  selectionMode?: 'single' | 'multiple';
  parentOnly?: boolean;
  excludeIds?: string[];
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onChange,
  placeholder,
  isInvalid = false,
  errorMessage,
  isRequired = false,
  label,
  variant = 'bordered',
  className,
  'aria-label': ariaLabel,
  showAllOption = false,
  showEmptyOption = !showAllOption,
  isDisabled = false,
  selectionMode = 'single',
  parentOnly = false,
  excludeIds = [],
}) => {
  const { t } = useTranslation('admin');
  // API returns tree structure by default, flat list when parentOnly=true
  const { data: categories = [], isLoading, error } = useCategories(parentOnly ? { parentOnly: true } : undefined);

  // Filter out excluded categories recursively from tree
  const filteredCategories = useMemo(() => {
    if (excludeIds.length === 0) return categories;
    const excludeSet = new Set(excludeIds);

    const filterTree = (cats: Category[]): Category[] => {
      return cats
        .filter((cat) => !excludeSet.has(cat.id))
        .map((cat) => ({
          ...cat,
          children: cat.children ? filterTree(cat.children) : undefined,
        }));
    };

    return filterTree(categories);
  }, [categories, excludeIds]);

  // Check if a category has children
  const hasChildren = (category: Category): boolean => {
    return !!category.children && category.children.length > 0;
  };

  // Recursively build nested items with proper indentation
  const buildNestedItems = (children: Category[], level: number): React.ReactElement[] => {
    const items: React.ReactElement[] = [];

    children.forEach((child) => {
      const paddingClass = level === 1 ? 'pl-4' : level === 2 ? 'pl-8' : level >= 3 ? 'pl-12' : '';
      const textClass = level > 0 ? 'text-gray-600 dark:text-gray-400' : '';

      items.push(
        <SelectItem key={child.id} textValue={child.name}>
          <span className={`${paddingClass} ${textClass}`.trim()}>{child.name}</span>
        </SelectItem>,
      );

      // Recursively add children
      if (hasChildren(child)) {
        items.push(...buildNestedItems(child.children!, level + 1));
      }
    });

    return items;
  };

  // Build section items for a parent category (parent + all descendants)
  const buildSectionItems = (parentCategory: Category): React.ReactElement[] => {
    return [
      <SelectItem key={parentCategory.id} textValue={parentCategory.name}>
        <span className="font-medium">{parentCategory.name}</span>
      </SelectItem>,
      ...buildNestedItems(parentCategory.children!, 1),
    ];
  };

  const renderSelectItems = () => {
    if (isLoading) {
      return <SelectItem key="loading">{t('common.loading')}</SelectItem>;
    }

    if (error) {
      return <SelectItem key="error">{(error as Error)?.message || 'Failed to load categories'}</SelectItem>;
    }

    // If parentOnly, render flat list (API returns flat list)
    if (parentOnly) {
      return (
        <>
          {filteredCategories.map((category) => (
            <SelectItem key={category.id}>{category.name}</SelectItem>
          ))}
        </>
      );
    }

    // Render categories in order from backend (alphabetical)
    return (
      <>
        {filteredCategories.map((category) =>
          hasChildren(category) ? (
            <SelectSection key={`section-${category.id}`} title={category.name} showDivider items={[]}>
              {buildSectionItems(category)}
            </SelectSection>
          ) : (
            <SelectItem key={category.id}>{category.name}</SelectItem>
          ),
        )}
      </>
    );
  };

  const selectLabel = label;
  const selectPlaceholder = placeholder || t('guides.modal.form.category.placeholder');
  const selectAriaLabel =
    ariaLabel ||
    t('guides.modal.form.category.label', { defaultValue: 'Category' }) ||
    (label === '' ? selectPlaceholder : undefined);

  return (
    <Select
      label={selectLabel}
      placeholder={selectPlaceholder}
      selectedKeys={value !== undefined && value !== null ? (Array.isArray(value) ? value : [value]) : []}
      onSelectionChange={(keys) => {
        if (selectionMode === 'multiple') {
          const selectedValues = Array.from(keys).map((key) => key as string);
          onChange(selectedValues);
        } else {
          const selectedValue = Array.from(keys)[0] as string;
          onChange(selectedValue ?? '');
        }
      }}
      isRequired={isRequired}
      variant={variant}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      isLoading={isLoading}
      startContent={isLoading ? <Spinner size="sm" /> : undefined}
      className={className}
      selectionMode={selectionMode}
      aria-label={selectAriaLabel || selectLabel || t('guides.modal.form.category.label', { defaultValue: 'Category' })}
      isDisabled={isDisabled}
    >
      {showAllOption ? <SelectItem key="all">{t('guides.filters.all')}</SelectItem> : null}
      {showEmptyOption ? <SelectItem key="">{t('common.none') || 'None'}</SelectItem> : null}
      {renderSelectItems()}
    </Select>
  );
};

export default CategorySelector;
